/**
 * Merge two lists of license-like objects into a single deduplicated array of license records.
 *
 * - `fallbackLicenses` entries supply a base “id/name/text” for each license.
 *   These will be converted into objects `{ fullName, shortName }` (both trimmed).
 * - `rawLicensesData` entries supply “fullName/shortName/aliases” for enriched license info.
 *
 * The merge rules are:
 * 1. First, include every `fallbackLicenses` entry exactly once, keyed by normalized (id || name || text),
 *    excluding any whose normalized key is empty or “NOASSERTION.” Each will be converted to
 *    `{ fullName: rawIdOrName, shortName: rawIdOrName }` (both trimmed).
 * 2. Next, for each `rawLicensesData` entry:
 *    – Compute all normalized candidate keys from (fullName, shortName, aliases).
 *    – If any candidate key is already present:
 *         • Immediately override the existing output at that index with a **trimmed** copy of this raw object.
 *      If no candidate key is present, append the **trimmed** raw object.
 * 3. All normalization removes non‐alphanumeric characters, trims, and uppercases. “NOASSERTION” is always skipped.
 *
 * Use in both React `useMemo(...)` or normal contexts:
 * ```
 * const licenses = useMemo(
 *   () => mergeLicensesAndLicensesData(fallbackLicenses, rawLicensesData),
 *   [fallbackLicenses, rawLicensesData]
 * );
 * ```
 *
 * @returns An array of objects. For any fallback‐derived license, each object has at least
 *          `{ fullName: string; shortName: string }` (both trimmed). For any raw‐derived license,
 *          the returned object is a trimmed copy of the original `rawLicensesData` entry.
 */
export default function mergeLicensesAndLicensesData<
  F extends { id?: string | null; name?: string | null; text?: string | null },
  R extends {
    fullName?: string | null;
    shortName?: string | null;
    aliases?: (string | null)[] | null;
  },
>(
  fallbackLicenses: F[],
  rawLicensesData: R[],
): Array<R & { fullName?: string; shortName?: string; aliases?: string[] }> {
  // Helper to normalize a string: remove non-alphanumeric, trim, uppercase.
  const normalizeKey = (s: string | undefined): string => {
    if (!s) return '';
    return s
      .replace(/[^a-zA-Z0-9]/g, '')
      .trim()
      .toUpperCase();
  };

  // Given a raw R, return a “trimmed” copy where fullName/shortName/aliases are .trim()-ed
  const makeTrimmedRaw = (
    lic: R,
  ): R & { fullName?: string; shortName?: string; aliases?: string[] } => {
    // Start by shallow-copying all properties of lic
    const trimmed = { ...(lic as object) } as R & {
      fullName?: string;
      shortName?: string;
      aliases?: string[];
    };

    // If fullName exists and is a string, trim it
    if (typeof lic.fullName === 'string') {
      trimmed.fullName = lic.fullName.trim();
      if (trimmed.fullName === '') {
        delete trimmed.fullName;
      }
    }

    // If shortName exists and is a string, trim it
    if (typeof lic.shortName === 'string') {
      trimmed.shortName = lic.shortName.trim();
      if (trimmed.shortName === '') {
        delete trimmed.shortName;
      }
    }

    // If aliases exists and is an array, trim each entry and remove empty strings
    if (Array.isArray(lic.aliases)) {
      const cleanAliases = lic.aliases
        .filter((a): a is string => typeof a === 'string')
        .map((a) => a.trim())
        .filter((a) => a !== '');
      if (cleanAliases.length > 0) {
        trimmed.aliases = cleanAliases;
      } else {
        delete trimmed.aliases;
      }
    }

    return trimmed;
  };

  // Map from normalized key -> index into `output`
  const seenIndex = new Map<string, number>();
  const output: Array<R & { fullName?: string; shortName?: string; aliases?: string[] }> =
    [];

  // 1) Process fallbackLicenses
  for (const lic of fallbackLicenses) {
    const rawIdOrName = (lic.id || lic.name || lic.text || '').trim();
    const key = normalizeKey(rawIdOrName);
    if (!key || key === 'NOASSERTION') continue;

    if (!seenIndex.has(key)) {
      // create a minimal object with trimmed fullName and shortName
      const obj: { fullName: string; shortName: string } = {
        fullName: rawIdOrName,
        shortName: rawIdOrName,
      };
      const idx = output.length;
      seenIndex.set(key, idx);
      output.push(
        obj as R & { fullName?: string; shortName?: string; aliases?: string[] },
      );
    }
  }

  // 2) Process rawLicensesData
  for (const lic of rawLicensesData) {
    // First, make a trimmed copy of this raw entry
    const trimmed = makeTrimmedRaw(lic);

    // Gather candidate raw strings (from the trimmed copy)
    const candFullRaw = trimmed.fullName || '';
    const candShortRaw = trimmed.shortName || '';
    const candAliasesRaw = Array.isArray(trimmed.aliases) ? trimmed.aliases : [];

    // Build candidate strings & keys from fullName, shortName, aliases
    const candidateStrings: string[] = [];
    if (candFullRaw) candidateStrings.push(candFullRaw);
    if (candShortRaw) candidateStrings.push(candShortRaw);
    candidateStrings.push(...candAliasesRaw);

    const candidateKeys = candidateStrings
      .map((s) => normalizeKey(s))
      .filter((k) => k && k !== 'NOASSERTION');

    if (candidateKeys.length === 0) continue;

    // Determine primary displayKey from fullName || shortName || first alias
    const displayRaw = candFullRaw || candShortRaw || candAliasesRaw[0] || '';
    const displayKey = normalizeKey(displayRaw);
    if (!displayKey || displayKey === 'NOASSERTION') continue;

    // Check if any candidateKey is already present
    let matchKey: string | null = null;
    for (const k of candidateKeys) {
      if (seenIndex.has(k)) {
        matchKey = k;
        break;
      }
    }

    if (matchKey !== null) {
      // Already exists: override whatever was there with this trimmed raw
      const idx = seenIndex.get(matchKey)!;
      output[idx] = trimmed;
      // Ensure every candidate key now maps to this index
      for (const k of candidateKeys) {
        seenIndex.set(k, idx);
      }
      continue;
    }

    // No existing match: append trimmed
    const newIndex = output.length;
    output.push(trimmed);
    for (const k of candidateKeys) {
      seenIndex.set(k, newIndex);
    }
  }

  return output;
}
